import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Rating, RatingProps, svgIconClasses, SvgIconProps } from '@mui/material';

const RatingSx = {
  [`& .${svgIconClasses.root}`]: {
    width: '0.5em',
    height: '0.5em',
  },
};

type StatusNubsProps = { color?: SvgIconProps['color'] } & Omit<RatingProps, 'color'>;

/**
 * Status indicator nubs
 *
 * Based on {@link https://v4.mui.com/components/rating/#customized-ratings MUI `Rating` component},
 * so shares the {@link https://v4.mui.com/api/rating/ same API}.
 */
const StatusNubs = ({
  sx,
  defaultValue = 0,
  max = 5,
  className,
  color = 'primary',
  ...props
}: StatusNubsProps) => {
  const { formatMessage } = useIntl();
  const getLabelText = useCallback(
    (value: number) => formatMessage({ defaultMessage: 'Status: {value}' }, { value }),
    [formatMessage]
  );
  return (
    <Rating
      sx={Array.isArray(sx) ? [RatingSx, ...sx] : [RatingSx, sx]}
      defaultValue={defaultValue}
      max={max}
      icon={<FiberManualRecordIcon color={color} fontSize="inherit" data-testid="custom-icon" />}
      emptyIcon={
        <FiberManualRecordIcon
          color="disabled"
          fontSize="inherit"
          data-testid="custom-disabled-icon"
        />
      }
      readOnly
      getLabelText={getLabelText}
      emptyLabelText={<FormattedMessage defaultMessage="No status" />}
      className={className}
      data-testid="status-nubs"
      {...props}
    />
  );
};

export type { StatusNubsProps };
export default StatusNubs;
